/**
 * Canonical garment size order (single source of truth).
 *
 * Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
 * https://trello.com/c/q7EQ3a0b -- pebble P6, plan Q4.
 *
 * The storefront product page renders a size x quantity matrix over the
 * `sizes[]` the product payload returns UNORDERED. Sort them by this list so
 * the matrix reads XS -> S -> M -> ... instead of alphabetical. Lifted from the
 * hardcoded array in ProductVariantSelector.vue (the only prior size-order
 * source) so both surfaces share one list. Unknown sizes are appended after the
 * known ones, in stable alphabetical order (never dropped).
 */
export const SIZE_ORDER = [
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "2XL",
  "3XL",
  "4XL",
  "5XL",
  "6XL",
  "LT",
  "XLT",
  "2XLT",
  "3XLT",
  "4XLT",
  "OSFA",
];

/**
 * Return a comparator index for a size label -- its position in SIZE_ORDER, or
 * a large sentinel for unknown sizes so they sort AFTER every known size.
 * Case-insensitive; whitespace-trimmed. The label is compared as-is otherwise.
 */
function sizeIndex(label) {
  const key = String(label == null ? "" : label).trim().toUpperCase();
  const idx = SIZE_ORDER.indexOf(key);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}

/**
 * Sort an array of size objects (`{ id, label }`) by SIZE_ORDER. Known sizes
 * come first in the canonical order; unknown sizes are appended in stable
 * alphabetical order by label. Returns a NEW array (does not mutate the input).
 */
export function sortSizes(sizes) {
  if (!Array.isArray(sizes)) return [];
  return [...sizes].sort((a, b) => {
    const ia = sizeIndex(a && a.label);
    const ib = sizeIndex(b && b.label);
    if (ia !== ib) return ia - ib;
    // Both unknown (or both same known index -- shouldn't happen): stable
    // alphabetical by label so the order is deterministic.
    const la = String((a && a.label) || "").toUpperCase();
    const lb = String((b && b.label) || "").toUpperCase();
    if (la < lb) return -1;
    if (la > lb) return 1;
    return 0;
  });
}
